import { io } from 'socket.io-client';
import { API, PARTICIPANT_BATCH_FLUSH_MS, REALTIME_TOKEN_URL } from '../config';
import { getDeviceId, apiFetch } from '../deviceId';

/**
 * Lightshow participant batcher.
 *
 * Buffers register/status/telemetry events and flushes them on a per-client
 * rolling timer over a Socket.io `participant:batch` event to the realtime
 * gateway. Each client's flush phase is randomised, so the synchronised
 * playing/completed transitions — which used to burst the Galaxy write-path —
 * arrive smoothly spread across the flush window and off Galaxy entirely.
 *
 * Best-effort throughout: when the gateway socket isn't connected (still
 * handshaking, gateway down, etc.) it falls back to the existing jittered HTTP
 * participant endpoints, so a degraded gateway only degrades the analytics
 * counts — never the show, which is driven by the HTTP/CDN schedule poll.
 *
 * The show control plane never touches this socket — telemetry only.
 */
export function createParticipantBatcher() {
  const deviceId = getDeviceId();

  let buffer = []; // [{ pipeId, showInstanceId, kind, status?, metadata?, telemetryData?, ts }]
  let socket = null;
  let connecting = false;
  let connectPipeId = null; // the pipe we opened the socket for (stable per URL)
  let lastShowInstanceId = null; // needed to attribute the socket-outcome report
  let socketOutcomeReported = false;
  let flushTimer = null;
  let started = false;

  function isConnected() {
    return !!socket && socket.connected;
  }

  /**
   * Report ONCE per client whether the gateway socket came up, and why not.
   *
   * Without this the fallback is invisible: during LKF on 2026-08-22, 493
   * clients registered while the gateway held 0-2 sockets for the pipe, i.e.
   * effectively the whole audience sat on the HTTP fallback and nothing said
   * so. The batch flush is best-effort by design, which is right — but it
   * meant the primary path could be dead without anyone noticing.
   *
   * Goes through the normal buffer, so it rides the socket when there is one
   * and the HTTP fallback when there is not — the failing case reports itself.
   */
  function reportSocketOutcome(outcome, reason) {
    if (socketOutcomeReported || !connectPipeId || !lastShowInstanceId) return;
    socketOutcomeReported = true;
    buffer.push({
      pipeId: connectPipeId,
      showInstanceId: lastShowInstanceId,
      kind: 'telemetry',
      telemetryData: {
        socketOutcome: outcome,
        ...(reason ? { socketReason: String(reason).slice(0, 200) } : {}),
      },
      ts: Date.now(),
    });
  }

  /** Open the gateway socket for this pipe (once). Best-effort, non-blocking. */
  async function ensureSocket(pipeId) {
    if (socket || connecting || !pipeId) return;
    connecting = true;
    connectPipeId = pipeId;
    try {
      const res = await apiFetch(REALTIME_TOKEN_URL(pipeId));
      if (!res.ok) throw new Error(`token ${res.status}`);
      const { token, endpoint } = await res.json();
      if (!token || !endpoint) throw new Error('no token/endpoint');

      socket = io(`${endpoint}/pipe-${pipeId}`, {
        auth: { token },
        transports: ['websocket'],
        reconnection: true,
        // Widened to spread reconnects after a task loss: the old 1000/10000
        // pair retried 500-1500 ms after a drop, so a task holding thousands of
        // sockets produced a handshake storm against ~125/s per-task capacity.
        reconnectionDelay: 2000,
        reconnectionDelayMax: 30000,
        randomizationFactor: 0.5,
        reconnectionAttempts: Infinity,
      });
      socket.on('connect', () => reportSocketOutcome('connected'));
      // Still non-fatal — the flush loop keeps using the HTTP fallback — but no
      // longer silent.
      socket.on('connect_error', (err) => reportSocketOutcome('failed', err?.message));
    } catch (err) {
      // Leave socket null; HTTP fallback carries the load until a later retry.
      socket = null;
      reportSocketOutcome('failed', err?.message);
    } finally {
      connecting = false;
    }
  }

  /** HTTP fallback for a single event — mirrors the original useParticipant POSTs. */
  async function postOne(ev) {
    try {
      const base = {
        pipeId: ev.pipeId,
        showInstanceId: ev.showInstanceId,
        deviceId,
      };
      let url;
      let body;
      if (ev.kind === 'register') {
        url = API.participant.register();
        body = base;
      } else if (ev.kind === 'status') {
        url = API.participant.status();
        body = { ...base, status: ev.status, metadata: ev.metadata || {} };
      } else if (ev.kind === 'telemetry') {
        url = API.participant.telemetry();
        body = { ...base, telemetryData: ev.telemetryData || {} };
      } else if (ev.kind === 'heartbeat') {
        url = API.participant.heartbeat();
        body = base;
      } else {
        return;
      }
      await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch {
      // Best-effort telemetry — drop on failure.
    }
  }

  /** Send a group of same-show events over the socket as one batch. */
  function emitBatch(showInstanceId, events) {
    socket.emit('participant:batch', {
      showInstanceId,
      events: events.map((ev) => ({
        kind: ev.kind,
        ...(ev.status !== undefined ? { status: ev.status } : {}),
        ...(ev.metadata !== undefined ? { metadata: ev.metadata } : {}),
        ...(ev.telemetryData !== undefined ? { telemetryData: ev.telemetryData } : {}),
        ts: ev.ts,
      })),
    });
  }

  /** Drain the buffer — socket batches grouped by show, else HTTP fallback. */
  function flush() {
    if (buffer.length === 0) return;
    const pending = buffer;
    buffer = [];

    if (isConnected()) {
      // Group by showInstanceId — a batch payload carries one show; the
      // gateway derives pipeId/deviceId from the authenticated socket.
      const byShow = new Map();
      for (const ev of pending) {
        const list = byShow.get(ev.showInstanceId) || [];
        list.push(ev);
        byShow.set(ev.showInstanceId, list);
      }
      for (const [showInstanceId, events] of byShow) {
        emitBatch(showInstanceId, events);
      }
    } else {
      // Fallback: fire each over HTTP (independent, best-effort).
      for (const ev of pending) postOne(ev);
    }
  }

  /**
   * Flush everything in a way that survives the page going away (MM #111).
   *
   * The ordinary flush uses a socket emit or fetch; browsers cancel both when a
   * navigation starts, so the existing pagehide flush is best-effort at best.
   * That is why the post-show redirect waited 12-20s — long enough for the
   * rolling 10s flush to have fired on its own. The cost of that safety margin
   * was the whole return audience: people pocket the phone inside 20s, iOS
   * suspends the timer, and the redirect never runs.
   *
   * sendBeacon is queued by the browser and delivered independently of the
   * page, which removes the reason for the wait. Falls back to the normal
   * flush where it is unavailable.
   *
   * @returns {boolean} true if everything was handed to sendBeacon
   */
  function flushBeacon() {
    if (buffer.length === 0) return true;
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) {
      flush();
      return false;
    }

    const pending = buffer;
    buffer = [];
    let allQueued = true;

    for (const ev of pending) {
      const base = { pipeId: ev.pipeId, showInstanceId: ev.showInstanceId, deviceId };
      let url;
      let body;
      if (ev.kind === 'register') {
        url = API.participant.register();
        body = base;
      } else if (ev.kind === 'status') {
        url = API.participant.status();
        body = { ...base, status: ev.status, metadata: ev.metadata || {} };
      } else if (ev.kind === 'telemetry') {
        url = API.participant.telemetry();
        body = { ...base, telemetryData: ev.telemetryData || {} };
      } else if (ev.kind === 'heartbeat') {
        url = API.participant.heartbeat();
        body = base;
      } else {
        continue;
      }
      try {
        // text/plain, not application/json: a JSON content-type makes the
        // beacon a preflighted cross-origin request, and preflight cannot
        // complete during unload. The endpoints parse the body regardless.
        const blob = new Blob([JSON.stringify(body)], { type: 'text/plain;charset=UTF-8' });
        if (!navigator.sendBeacon(url, blob)) allQueued = false;
      } catch {
        allQueued = false;
      }
    }
    return allQueued;
  }

  /** Start the rolling flush loop with a randomised initial phase. */
  function start() {
    if (started) return;
    started = true;
    // Randomise the first flush so clients that joined together don't align
    // their flush phases — this is what desynchronises the bursts.
    const initialDelay = Math.random() * PARTICIPANT_BATCH_FLUSH_MS;
    setTimeout(() => {
      flush();
      flushTimer = setInterval(flush, PARTICIPANT_BATCH_FLUSH_MS);
    }, initialDelay);

    if (typeof document !== 'undefined') {
      // Flush on the way out so completed/telemetry aren't lost to the
      // post-show redirect / tab close.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush();
      });
      window.addEventListener('pagehide', flush);
    }
  }

  /** Enqueue an event; lazily opens the socket and starts the loop. */
  function enqueue(ev) {
    if (!ev.pipeId || !ev.showInstanceId) return;
    buffer.push({ ...ev, ts: Date.now() });
    lastShowInstanceId = ev.showInstanceId;
    start();
    ensureSocket(ev.pipeId);
  }

  return {
    deviceId,
    register(pipeId, showInstanceId) {
      enqueue({ kind: 'register', pipeId, showInstanceId });
    },
    status(pipeId, showInstanceId, status, metadata) {
      enqueue({ kind: 'status', pipeId, showInstanceId, status, metadata });
    },
    telemetry(pipeId, showInstanceId, telemetryData) {
      enqueue({ kind: 'telemetry', pipeId, showInstanceId, telemetryData });
    },
    heartbeat(pipeId, showInstanceId) {
      enqueue({ kind: 'heartbeat', pipeId, showInstanceId });
    },
    // Exposed for tests / debugging.
    /** Unload-safe flush — see flushBeacon. */
    flushBeacon,
    _flush: flush,
    _isConnected: isConnected,
    _connectPipeId: () => connectPipeId,
  };
}
